// Service charge installments — shared by the Accountant and Recruiter dashboards.
// The Accountant decides Full or 2 installments; installment 1 is collected by HR through the normal
// "payment received" step, the Accountant records the balance. The server sends reminders.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fmtDate } from '../utils/dates';

const token = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });
export const PAYMENT_MODES = ['UPI', 'Cash', 'Bank Transfer', 'Cheque', 'Card', 'Other'];

export const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const todayISO = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const addDays = (iso, days) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
const daysBetween = (fromIso, toIso) => Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86400000);
const dialable = (v) => { const d = String(v || '').replace(/\D/g, ''); return d.length === 10 ? `91${d}` : d; };

export const emptyPlan = () => ({ payment_plan: 'Full', first_amount: '', first_due_date: todayISO(), second_due_date: '', reminder_date: '' });

// Client-side check mirroring the server rules, so the Accountant sees problems before saving.
export const planError = (plan, total, firstPaid = false) => {
  if (plan.payment_plan !== 'Installment') return '';
  const first = Number(plan.first_amount);
  if (!firstPaid && (!(first > 0) || first >= total)) return `1st installment must be more than ₹0 and less than ${rupees(total)}`;
  if (!plan.second_due_date) return 'Set the 2nd installment due date';
  if (!plan.reminder_date) return 'Set the reminder date';
  if (plan.reminder_date > plan.second_due_date) return 'Reminder must be on or before the due date';
  if (!firstPaid && plan.second_due_date <= (plan.first_due_date || todayISO())) return '2nd installment must be due after the 1st';
  return '';
};

const inputCls = 'w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400';

export const PaymentPlanFields = ({ total, plan, onChange, firstPaid = null }) => {
  const set = (k, v) => onChange({ ...plan, [k]: v });
  const first = firstPaid ? Number(firstPaid.paid_amount ?? firstPaid.amount) : Number(plan.first_amount) || 0;
  const balance = Math.max(0, Math.round((total - first) * 100) / 100);
  const quickSplit = (pct) => set('first_amount', String(Math.round((total * pct) / 100)));
  const setSecondDue = (v) => {
    // Default the reminder to 3 days before the due date (never before today)
    const suggested = v ? [addDays(v, -3), todayISO()].sort().pop() : '';
    onChange({ ...plan, second_due_date: v, reminder_date: !plan.reminder_date || plan.reminder_date > v ? (suggested > v ? v : suggested) : plan.reminder_date });
  };
  return (
    <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-black text-gray-700">💳 Payment plan</span>
        {!firstPaid && (
          <div className="inline-flex rounded-xl bg-white p-0.5 ring-1 ring-gray-200">
            {[['Full', 'Full payment'], ['Installment', '2 installments']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set('payment_plan', v)}
                className={`rounded-lg px-3 py-1 text-xs font-bold transition ${plan.payment_plan === v ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {plan.payment_plan === 'Installment' && (
        <>
          {firstPaid ? (
            <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">✅ 1st installment {rupees(first)} received on {fmtDate(firstPaid.paid_on)}</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-black text-gray-600 mb-1">1st installment (₹) — collect now</label>
                <input type="number" min="1" value={plan.first_amount} onChange={(e) => set('first_amount', e.target.value)} className={inputCls} placeholder="e.g. 2500" />
                <div className="mt-1 flex gap-1">
                  {[50, 60, 70].map((p) => <button key={p} type="button" onClick={() => quickSplit(p)} className="rounded-lg bg-white px-2 py-0.5 text-[10px] font-bold text-orange-700 ring-1 ring-orange-200 hover:bg-orange-100">{p}%</button>)}
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-black text-gray-600 mb-1">1st due date</label>
                <input type="date" value={plan.first_due_date} onChange={(e) => set('first_due_date', e.target.value)} className={inputCls} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-black text-gray-600 mb-1">2nd installment due date</label>
              <input type="date" min={todayISO()} value={plan.second_due_date} onChange={(e) => setSecondDue(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-[11px] font-black text-gray-600 mb-1">Reminder date</label>
              <input type="date" min={todayISO()} max={plan.second_due_date || undefined} value={plan.reminder_date} onChange={(e) => set('reminder_date', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="rounded-xl bg-white px-3 py-2 text-xs ring-1 ring-orange-100">
            <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-bold">{rupees(total)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">1st installment</span><span className="font-bold">{rupees(first)}</span></div>
            <div className="flex justify-between border-t border-orange-100 mt-1 pt-1"><span className="text-gray-700 font-bold">2nd installment (balance)</span><span className="font-black text-orange-700">{rupees(balance)}</span></div>
            <p className="mt-1.5 text-[11px] text-gray-500">On the reminder date the Recruiter, HR and Accounts are notified and the candidate gets an email. Another notice goes out if the due date passes unpaid.</p>
          </div>
        </>
      )}
    </div>
  );
};

const ModalShell = ({ title, subtitle, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl">
      <div className="sticky top-0 flex items-center justify-between rounded-t-3xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-4">
        <div><p className="text-xs font-extrabold uppercase text-white/80">{title}</p><h3 className="font-black text-white">{subtitle}</h3></div>
        <button type="button" onClick={onClose} className="rounded-xl bg-white/20 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/30">✕</button>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  </div>
);

// Set or change the plan on an existing request (after the proforma exists)
export const PaymentPlanModal = ({ req, API_URL, onClose, onSaved }) => {
  const summary = req.installment_summary || {};
  const firstPaid = (summary.installments || []).find((i) => i.installment_no === 1 && i.status === 'Paid') || null;
  const second = (summary.installments || []).find((i) => i.installment_no === 2) || null;
  const firstInst = (summary.installments || []).find((i) => i.installment_no === 1) || null;
  const [plan, setPlan] = useState(() => ({
    ...emptyPlan(),
    payment_plan: summary.plan || 'Full',
    first_amount: firstInst ? String(firstInst.amount) : '',
    first_due_date: firstInst?.due_date || todayISO(),
    second_due_date: second?.due_date || '',
    reminder_date: second?.reminder_date || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const total = Number(summary.total || 0);
  const save = async () => {
    const e = planError(plan, total, Boolean(firstPaid));
    if (e) { setError(e); return; }
    setSaving(true); setError('');
    try {
      await axios.put(`${API_URL}/api/accountant/payment-requests/${req.id}/payment-plan`, plan, { headers: authHeader() });
      onSaved();
    } catch (err) { setError(err?.response?.data?.error || 'Failed to save the plan.'); setSaving(false); }
  };
  return (
    <ModalShell title={firstPaid ? 'Balance installment' : 'Payment plan'} subtitle={req.member_name} onClose={onClose}>
      <PaymentPlanFields total={total} plan={plan} onChange={setPlan} firstPaid={firstPaid} />
      {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-600">{error}</p>}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="rounded-xl bg-orange-500 px-6 py-2 text-sm font-extrabold text-white hover:bg-orange-600 disabled:opacity-60">{saving ? 'Saving…' : 'Save plan'}</button>
      </div>
    </ModalShell>
  );
};

// Record (or waive) the balance installment
export const RecordInstallmentModal = ({ inst, memberName, API_URL, onClose, onSaved }) => {
  const [mode, setMode] = useState('record'); // record | waive
  const [form, setForm] = useState({ paid_on: todayISO(), payment_mode: 'UPI', payment_reference: '' });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try {
      if (mode === 'record') await axios.post(`${API_URL}/api/accountant/installments/${inst.id}/mark-paid`, form, { headers: authHeader() });
      else await axios.post(`${API_URL}/api/accountant/installments/${inst.id}/waive`, { reason }, { headers: authHeader() });
      onSaved();
    } catch (err) { setError(err?.response?.data?.error || 'Failed to save.'); setSaving(false); }
  };
  return (
    <ModalShell title={`Installment ${inst.installment_no}`} subtitle={`${memberName} · ${rupees(inst.amount)}`} onClose={onClose}>
      <div className="inline-flex rounded-xl bg-gray-100 p-0.5">
        {[['record', '✅ Record payment'], ['waive', '🚫 Waive balance']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setMode(v)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${mode === v ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-gray-500">Due {fmtDate(inst.due_date)}{inst.reminder_date ? ` · reminder ${fmtDate(inst.reminder_date)}` : ''}</p>
      {mode === 'record' ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-[11px] font-black text-gray-600 mb-1">Paid on</span>
              <input type="date" max={todayISO()} value={form.paid_on} onChange={(e) => setForm((f) => ({ ...f, paid_on: e.target.value }))} className={inputCls} />
            </label>
            <label className="block"><span className="block text-[11px] font-black text-gray-600 mb-1">Mode</span>
              <select value={form.payment_mode} onChange={(e) => setForm((f) => ({ ...f, payment_mode: e.target.value }))} className={inputCls}>
                {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
              </select>
            </label>
          </div>
          <label className="block"><span className="block text-[11px] font-black text-gray-600 mb-1">Reference / UTR (optional)</span>
            <input value={form.payment_reference} onChange={(e) => setForm((f) => ({ ...f, payment_reference: e.target.value }))} className={inputCls} placeholder="UPI ref, cheque no…" />
          </label>
        </>
      ) : (
        <label className="block"><span className="block text-[11px] font-black text-gray-600 mb-1">Why is the balance waived?</span>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="e.g. Member resigned in the first week" />
          <span className="mt-1 block text-[11px] text-gray-500">Reminders stop and the final invoice will cover only what was collected.</span>
        </label>
      )}
      {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-600">{error}</p>}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
        <button type="button" onClick={save} disabled={saving || (mode === 'waive' && reason.trim().length < 3)}
          className={`rounded-xl px-6 py-2 text-sm font-extrabold text-white disabled:opacity-60 ${mode === 'record' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-800 hover:bg-gray-900'}`}>
          {saving ? 'Saving…' : mode === 'record' ? 'Record payment' : 'Waive balance'}
        </button>
      </div>
    </ModalShell>
  );
};

// Compact plan progress for a request row
export const InstallmentProgress = ({ summary }) => {
  if (!summary || summary.plan !== 'Installment' || !summary.installments?.length) return null;
  return (
    <div className="mt-1.5 space-y-0.5">
      {summary.installments.map((i) => (
        <p key={i.id} className={`text-[11px] font-semibold ${i.status === 'Paid' ? 'text-emerald-700' : i.status === 'Waived' ? 'text-gray-400 line-through' : 'text-orange-700'}`}>
          {i.status === 'Paid' ? '✓' : i.status === 'Waived' ? '—' : '○'} #{i.installment_no} {rupees(i.amount)} · {i.status === 'Paid' ? `paid ${fmtDate(i.paid_on)}` : i.status === 'Waived' ? 'waived' : `due ${fmtDate(i.due_date)}`}
        </p>
      ))}
    </div>
  );
};

export const reminderMessage = (due, overdue) => [
  `Dear ${due.member_name},`,
  '',
  overdue
    ? `This is a reminder that the balance installment of your placement service charge (${rupees(due.amount)}) was due on ${fmtDate(due.due_date)} and is still pending.`
    : `This is a friendly reminder that the balance installment of your placement service charge (${rupees(due.amount)}) is due on ${fmtDate(due.due_date)}.`,
  '',
  'Please share the payment screenshot after paying. If you have already paid, kindly ignore this message.',
  '',
  '– Dani Job Club',
].join('\n');

// Balance installments still to collect — grouped Overdue / Due soon / Upcoming
export const InstallmentDuesPanel = ({ API_URL, endpoint, canRecord = false, refreshKey = 0, onChanged, hideWhenEmpty = false }) => {
  const [data, setData] = useState({ today: todayISO(), dues: [] });
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}${endpoint}`, { headers: authHeader() }); setData(r.data || { today: todayISO(), dues: [] }); }
    catch { /* keep last data */ }
    finally { setLoading(false); }
  }, [API_URL, endpoint]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load, refreshKey]);

  const groups = useMemo(() => {
    const overdue = [], soon = [], upcoming = [];
    data.dues.forEach((d) => {
      const days = daysBetween(data.today, d.due_date);
      if (days < 0) overdue.push({ ...d, days });
      else if ((d.reminder_date && d.reminder_date <= data.today) || days <= 7) soon.push({ ...d, days });
      else upcoming.push({ ...d, days });
    });
    return [['Overdue', overdue, 'rose'], ['Due soon', soon, 'amber'], ['Upcoming', upcoming, 'slate']];
  }, [data]);

  if (loading || (hideWhenEmpty && data.dues.length === 0)) return null;
  const total = data.dues.reduce((s, d) => s + Number(d.amount || 0), 0);
  const tone = { rose: 'bg-rose-50 ring-rose-200 text-rose-700', amber: 'bg-amber-50 ring-amber-200 text-amber-700', slate: 'bg-gray-50 ring-gray-200 text-gray-600' };

  return (
    <div className="rounded-2xl border border-orange-200 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 text-white">
        <div>
          <p className="text-sm font-black">💳 Installment dues</p>
          <p className="text-xs text-white/85">{data.dues.length ? `${data.dues.length} balance payment${data.dues.length === 1 ? '' : 's'} · ${rupees(total)} to collect` : 'No balance payments pending'}</p>
        </div>
        <div className="flex gap-1.5 text-[11px] font-black">
          {groups.map(([label, list]) => list.length > 0 && <span key={label} className="rounded-full bg-white/20 px-2 py-0.5">{list.length} {label}</span>)}
        </div>
      </div>
      {data.dues.length > 0 && (
        <div className="divide-y divide-gray-100">
          {groups.map(([label, list, t]) => list.map((d) => {
            const phone = dialable(d.contact_number);
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ring-1 ${tone[t]}`}>
                  {label === 'Overdue' ? `${-d.days}d overdue` : d.days === 0 ? 'Due today' : `in ${d.days}d`}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-gray-900">{d.member_name}{d.member_id ? <span className="ml-1.5 text-[11px] font-semibold text-gray-400">#{d.member_id}</span> : null}</p>
                  <p className="truncate text-xs text-gray-500">
                    {rupees(d.amount)} · due {fmtDate(d.due_date)}{d.job_title ? ` · ${d.job_title}${d.job_company ? ` @ ${d.job_company}` : ''}` : ''}{d.recruiter_name ? ` · ${d.recruiter_name}` : ''}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  {phone && (
                    <a href={`https://wa.me/${phone}?text=${encodeURIComponent(reminderMessage(d, label === 'Overdue'))}`} target="_blank" rel="noopener noreferrer"
                      className="rounded-xl border border-green-300 bg-green-50 px-2.5 py-1 text-xs font-bold text-green-800 hover:bg-green-100">💬 Remind</a>
                  )}
                  {phone && <a href={`tel:${String(d.contact_number).replace(/\D/g, '')}`} className="rounded-xl border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50" title="Call">📞</a>}
                  {canRecord && <button type="button" onClick={() => setRecording(d)} className="rounded-xl bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white hover:bg-emerald-700">Record</button>}
                </div>
              </div>
            );
          }))}
        </div>
      )}
      {recording && (
        <RecordInstallmentModal inst={recording} memberName={recording.member_name} API_URL={API_URL}
          onClose={() => setRecording(null)}
          onSaved={() => { setRecording(null); load(); if (onChanged) onChanged(); }} />
      )}
    </div>
  );
};
